import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { inspectPermissions, type PermissionRow } from "@/lib/permissions.functions";
import { getMyReadAudit } from "@/lib/binder.functions";
import { useSession } from "@/lib/user-data";

export const Route = createFileRoute("/permissions-inspector")({
  head: () => ({
    meta: [
      { title: "Permissions Inspector — Pal Stars" },
      { name: "description", content: "Internal Pal Stars tool: review row-level security policies, table grants and the read audit trail for your own collection." },
      { property: "og:title", content: "Pal Stars permissions inspector" },
      { property: "og:description", content: "Row-level security policies, grants and read audit trail." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/permissions-inspector" }],
  }),
  component: PermissionsInspectorPage,
});

interface AuditRow {
  id: string;
  action: string;
  actor_id: string | null;
  detail: unknown;
  created_at: string;
}

function PermissionsInspectorPage() {
  const { session, loading } = useSession();
  const fetchPerms = useServerFn(inspectPermissions);
  const fetchAudit = useServerFn(getMyReadAudit);
  const [rows, setRows] = useState<PermissionRow[] | null>(null);
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (!session) return;
    fetchPerms()
      .then((r) => setRows(r as PermissionRow[]))
      .catch(() => setError("The permissions helper could not be reached."));
    fetchAudit()
      .then((r) => setAudit(r as unknown as AuditRow[]))
      .catch(() => undefined);
  }, [session, fetchPerms, fetchAudit]);

  const grouped = useMemo(() => {
    const map = new Map<string, PermissionRow[]>();
    (rows ?? [])
      .filter((r) => !filter || r.table_name.includes(filter.toLowerCase()))
      .forEach((r) => {
        const list = map.get(r.table_name) ?? [];
        list.push(r);
        map.set(r.table_name, list);
      });
    return [...map.entries()];
  }, [rows, filter]);

  if (loading) return <div className="mx-auto max-w-3xl px-4 py-16 text-muted-foreground">Loading…</div>;

  if (!session) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <h1 className="text-3xl font-bold">Permissions inspector</h1>
        <p className="mt-3 text-sm text-muted-foreground">Sign in to inspect table access rules and your read audit trail.</p>
        <Link to="/auth" className="mt-5 inline-block rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:brightness-110">
          Sign in
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8 space-y-8">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Internal tooling</p>
        <h1 className="mt-2 text-3xl font-bold">Permissions inspector</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Every table in the app database with its row-level security state, policies and role grants — plus who has
          read your collection.
        </p>
      </header>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <input
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        aria-label="Filter tables"
        placeholder="Filter tables…"
        className="w-full max-w-xs rounded-md border bg-background px-3 py-2 text-sm"
      />

      <section className="space-y-4">
        {grouped.map(([table, policies]) => (
          <div key={table} className="glass rounded-xl p-4">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-bold">{table}</h2>
              <span className={`rounded-full px-2 py-0.5 text-xs ${policies[0].rls_enabled ? "bg-primary/15 text-primary" : "bg-destructive/15 text-destructive"}`}>
                {policies[0].rls_enabled ? "RLS enabled" : "RLS DISABLED"}
              </span>
              <span className="text-xs text-muted-foreground">{policies[0].grants ?? "no grants"}</span>
            </div>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="py-1 pr-3">Policy</th>
                    <th className="py-1 pr-3">Command</th>
                    <th className="py-1 pr-3">Roles</th>
                    <th className="py-1 pr-3">USING</th>
                    <th className="py-1">WITH CHECK</th>
                  </tr>
                </thead>
                <tbody>
                  {policies.map((p, i) => (
                    <tr key={`${table}-${p.policy_name ?? i}`} className="border-t border-border/50">
                      <td className="py-1 pr-3 font-medium">{p.policy_name ?? "— none —"}</td>
                      <td className="py-1 pr-3">{p.command ?? "—"}</td>
                      <td className="py-1 pr-3">{(p.roles ?? []).join(", ") || "—"}</td>
                      <td className="py-1 pr-3 font-mono">{p.using_expr ?? "—"}</td>
                      <td className="py-1 font-mono">{p.check_expr ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
        {rows && rows.length === 0 && <p className="text-sm text-muted-foreground">No tables returned.</p>}
      </section>

      <section>
        <h2 className="text-xl font-bold">Read audit trail for your collection</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Recorded whenever another member or a share link opens your binder. Only you can see these entries.
        </p>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-muted-foreground">
              <tr>
                <th className="py-1 pr-3">When</th>
                <th className="py-1 pr-3">Action</th>
                <th className="py-1">Viewer</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((a) => (
                <tr key={a.id} className="border-t border-border/50">
                  <td className="py-1 pr-3">{new Date(a.created_at).toLocaleString()}</td>
                  <td className="py-1 pr-3">{a.action.replace(/_/g, " ")}</td>
                  <td className="py-1 font-mono">{a.actor_id ? `${a.actor_id.slice(0, 8)}…` : "anonymous link"}</td>
                </tr>
              ))}
              {audit.length === 0 && (
                <tr><td colSpan={3} className="py-2 text-muted-foreground">No reads recorded yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
